import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  FlatLocation,
  LatLng,
  nearestVenue,
  partnerColor,
} from "../_constants/where-to-try-constants";
import { useTrans } from "@/utils/useTrans";
import { Spinner } from "@/v2-components/ui/spinner";

export interface ArcadeMapHandle {
  // 초기/geo 프레이밍 — 시트에 가려진 하단 높이(px)만큼 패딩 주고 전 매장(또는 you+nearest) fitBounds.
  refit: (bottomPx?: number) => void;
  // 시트 높이 변화 시 — 줌/타깃 보존한 채 가려진 높이 변화량의 절반만큼 panBy 로 재중심(§7-5).
  // 보는 위치를 바꾸지 않고, 가려지지 않은 영역 기준으로 계속 가운데 정렬 유지.
  nudgeForCover: (bottomPx: number) => void;
}

interface ArcadeMapProps {
  locations: FlatLocation[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  // "Use my location" 정밀 좌표. here 펄스 마커 + nearest 프레이밍에 사용.
  userLoc?: LatLng | null;
  // 부모(모바일 map 스테이지)가 높이를 통제할 때 true → 고정 높이/라운딩 대신 h-full.
  fill?: boolean;
  // 제스처(프로토 §5-2): 데스크톱/풀스크린=greedy(직접 휠·1핑거 팬), 모바일 밴드=none(정적, 탭 확장).
  gesture?: "greedy" | "none" | "cooperative" | "auto";
}

const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY ?? "";

let optionsSet = false;
function ensureOptions() {
  if (!optionsSet) {
    // marker 라이브러리는 아직 불필요(레거시 Marker + OverlayView 펄스). mapId 도입 시 marker 추가.
    setOptions({ key: API_KEY, libraries: ["maps"] });
    optionsSet = true;
  }
}

// 미니멀 라이트 베이스맵(Positron 유사). mapId 없이 인라인 styles[] 로 적용 — mapId 도입 시
// styles[] 는 무시되므로 그때 Cloud 스타일로 이관(가이드 §5-6).
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#f5f6f7" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a8f96" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#e6efe6" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#f0f1f3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#e6e8eb" }],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#cfe1ec" }],
  },
];

const BASE_PADDING = 24;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function markerIcon(color: string, dim = false): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 7,
    fillColor: color,
    fillOpacity: dim ? 0.4 : 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };
}

// google.maps 로드 후에만 OverlayView 서브클래싱 가능 → 지연 생성(모듈 스코프 참조 금지).
type HereOverlay = google.maps.OverlayView & { setLatLng: (p: LatLng) => void };
let HereOverlayCtor: (new (position: LatLng) => HereOverlay) | null = null;
function getHereOverlayCtor(): new (position: LatLng) => HereOverlay {
  if (HereOverlayCtor) return HereOverlayCtor;
  class Impl extends google.maps.OverlayView {
    private position: LatLng;
    private div: HTMLDivElement | null = null;
    constructor(position: LatLng) {
      super();
      this.position = position;
    }
    onAdd() {
      const d = document.createElement("div");
      d.className = "wte-here-dot";
      this.div = d;
      // floatPane = 최상위(마커 위). 클러스터러와 무관한 독립 레이어.
      this.getPanes()?.floatPane.appendChild(d);
    }
    draw() {
      if (!this.div) return;
      const p = this.getProjection()?.fromLatLngToDivPixel(
        new google.maps.LatLng(this.position.lat, this.position.lng),
      );
      if (p) {
        this.div.style.left = `${p.x}px`;
        this.div.style.top = `${p.y}px`;
      }
    }
    onRemove() {
      this.div?.remove();
      this.div = null;
    }
    setLatLng(pos: LatLng) {
      this.position = pos;
      this.draw();
    }
  }
  HereOverlayCtor = Impl as unknown as new (position: LatLng) => HereOverlay;
  return HereOverlayCtor;
}

// bounds 로 재핏 + maxZoom 12 캡(fitBounds 는 maxZoom 옵션 없음 → 1회성 idle 로 캡, 가이드 §5-2).
function fitWithCap(
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds,
  padding: google.maps.Padding,
  maxZoom = 12,
) {
  map.fitBounds(bounds, padding);
  const once = google.maps.event.addListenerOnce(map, "idle", () => {
    if ((map.getZoom() ?? 0) > maxZoom) map.setZoom(maxZoom);
  });
  // 안전: 리스너 누수 방지용 타임아웃 제거는 idle 1회 소비로 충분.
  void once;
}

const ArcadeMap = forwardRef<ArcadeMapHandle, ArcadeMapProps>(
  function ArcadeMap(
    { locations, selectedId, onSelect, userLoc, fill, gesture = "greedy" },
    ref,
  ) {
    const { t } = useTrans(["where-to-try"]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const hereRef = useRef<HereOverlay | null>(null);
    const infoRef = useRef<google.maps.InfoWindow | null>(null);
    // 현재 시트에 가려진 하단 높이(px). nudgeForCover 가 변화량 절반만큼 panBy 로 재중심.
    const coveredRef = useRef(0);
    // 최신 locations/userLoc 를 imperative refit 에서 참조하기 위한 미러.
    const locationsRef = useRef(locations);
    const userLocRef = useRef<LatLng | null>(userLoc ?? null);
    locationsRef.current = locations;
    userLocRef.current = userLoc ?? null;

    const [ready, setReady] = useState(false);

    // 현재 마커 + userLoc 를 담는 bounds. 없으면 null.
    const computeBounds = (bottomPx = 0) => {
      const map = mapRef.current;
      if (!map) return;
      const locs = locationsRef.current;
      const uloc = userLocRef.current;
      const bounds = new google.maps.LatLngBounds();
      let n = 0;
      if (uloc) {
        // located: you + nearest-1 타이트 프레이밍(가이드 §5-1).
        const near = nearestVenue(locs, uloc);
        bounds.extend({ lat: uloc.lat, lng: uloc.lng });
        if (
          near &&
          typeof near.lat === "number" &&
          typeof near.lng === "number"
        )
          bounds.extend({ lat: near.lat, lng: near.lng });
        n = near ? 2 : 1;
      } else {
        for (const loc of locs) {
          if (typeof loc.lat === "number" && typeof loc.lng === "number") {
            bounds.extend({ lat: loc.lat, lng: loc.lng });
            n++;
          }
        }
      }
      if (n === 0) {
        map.setCenter(DEFAULT_MAP_CENTER);
        map.setZoom(DEFAULT_MAP_ZOOM);
        return;
      }
      fitWithCap(map, bounds, {
        top: BASE_PADDING,
        left: BASE_PADDING,
        right: BASE_PADDING,
        bottom: bottomPx + BASE_PADDING,
      });
    };

    useImperativeHandle(ref, () => ({
      refit: (bottomPx = 0) => {
        if (!mapRef.current) return;
        google.maps.event.trigger(mapRef.current, "resize");
        coveredRef.current = bottomPx;
        computeBounds(bottomPx);
      },
      nudgeForCover: (bottomPx: number) => {
        const map = mapRef.current;
        if (!map) return;
        // 시트 높이만 바뀜 → 리사이즈 반영 후, 가려진 높이 변화량의 절반만큼 세로 panBy.
        // 줌/타깃은 그대로 → "보고 있던 지점"이 가려지지 않은 영역 중앙에 계속 유지된다.
        google.maps.event.trigger(map, "resize");
        const delta = bottomPx - coveredRef.current;
        coveredRef.current = bottomPx;
        if (delta !== 0) map.panBy(0, delta / 2);
      },
    }));

    // 마커 (재)구성 + 초기 핏. locations 변경마다.
    useEffect(() => {
      if (!containerRef.current || !API_KEY) return;
      ensureOptions();

      let cancelled = false;
      const listeners: google.maps.MapsEventListener[] = [];

      (async () => {
        const { Map } = (await importLibrary(
          "maps",
        )) as google.maps.MapsLibrary;
        if (cancelled || !containerRef.current) return;

        if (!mapRef.current) {
          mapRef.current = new Map(containerRef.current, {
            center: DEFAULT_MAP_CENTER,
            zoom: DEFAULT_MAP_ZOOM,
            // 프로토=컨트롤 전무(지도=방향 인덱스). 기본 UI 끄되 a11y 위해 키보드는 유지(§5-6).
            disableDefaultUI: true,
            keyboardShortcuts: true,
            clickableIcons: false,
            minZoom: 2,
            gestureHandling: gesture,
            styles: MAP_STYLES,
          });
          infoRef.current = new google.maps.InfoWindow();
        }
        const map = mapRef.current;

        clustererRef.current?.clearMarkers();
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current.clear();

        for (const loc of locations) {
          if (typeof loc.lat !== "number" || typeof loc.lng !== "number")
            continue;
          const marker = new google.maps.Marker({
            position: { lat: loc.lat, lng: loc.lng },
            title: loc.venue ?? loc.city,
            icon: markerIcon(partnerColor(loc.partnerId)),
          });
          // 마커 클릭 = 양방향(§5-5): 리스트/시트 행 선택(selectedId) → 이펙트가 팝업/pan 처리.
          listeners.push(marker.addListener("click", () => onSelect(loc.id)));
          markersRef.current.set(loc.id, marker);
        }

        const allMarkers = Array.from(markersRef.current.values());
        if (clustererRef.current) clustererRef.current.addMarkers(allMarkers);
        else
          clustererRef.current = new MarkerClusterer({
            map,
            markers: allMarkers,
          });

        computeBounds(0);
        if (!cancelled) setReady(true);
      })();

      return () => {
        cancelled = true;
        listeners.forEach((l) => google.maps.event.removeListener(l));
      };
      // gesture 는 최초 map 생성 시 초깃값으로만 사용 — 변경 반영은 별도 effect. 재빌드 churn 회피.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, onSelect]);

    // 행/마커 선택 → 라벨 팝업(이름+도시, CTA 없음, §5-5) + pan + zoom-floor. 해제 시 팝업 닫기.
    useEffect(() => {
      const map = mapRef.current;
      const info = infoRef.current;
      if (!map) return;
      if (!selectedId) {
        info?.close();
        return;
      }
      const loc = locations.find((l) => l.id === selectedId);
      if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number")
        return;
      if (info) {
        // 이름+도시 두 줄만(CTA/링크 없음 — 리스트 행 소유). Google 기본 헤더(빈 상단 여백 + 큰 X)를
        // 끄고(headerDisabled) 콤팩트 콘텐츠 + 작은 커스텀 × 로 프로토 팝업 룩 재현.
        const name = loc.venue ?? loc.city;
        const city = [loc.city, loc.country].filter(Boolean).join(", ");
        const el = document.createElement("div");
        // 닫기 버튼 공간 확보용 오른쪽 패딩. 음수 오프셋 금지(오버플로→스크롤바·클리핑 원인).
        el.style.cssText =
          "position:relative;min-width:150px;padding:0 22px 0 0;font:400 12.5px/1.4 system-ui,-apple-system,sans-serif;color:#697077";
        el.innerHTML =
          `<div style="font-weight:600;font-size:14px;color:#121619">${escapeHtml(name)}</div>` +
          `<div>${escapeHtml(city)}</div>`;
        const close = document.createElement("button");
        close.type = "button";
        close.setAttribute("aria-label", "Close");
        close.textContent = "×";
        // 콘텐츠 안쪽(양수 오프셋)에 배치 → 오버플로/클리핑 없음. 작은 X.
        close.style.cssText =
          "position:absolute;top:0;right:0;width:16px;height:16px;padding:0;border:0;background:transparent;color:#697077;font-size:15px;line-height:16px;text-align:center;cursor:pointer";
        close.addEventListener("click", () => onSelect(null));
        el.appendChild(close);
        info.setOptions({
          headerDisabled: true,
        } as google.maps.InfoWindowOptions);
        info.setContent(el);
        info.setPosition({ lat: loc.lat, lng: loc.lng });
        info.open({ map });
      }
      map.panTo({ lat: loc.lat, lng: loc.lng });
      if ((map.getZoom() ?? 0) < 11) map.setZoom(11);
      // 시트가 가리는 만큼 위로 올려 선택 매장을 "가시영역" 중앙에(전체 뷰포트 중앙 아님).
      if (coveredRef.current > 0) map.panBy(0, coveredRef.current / 2);
    }, [selectedId, locations, onSelect]);

    // userLoc → here 펄스 마커(OverlayView) 갱신 + you+nearest 프레이밍.
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      if (!userLoc) {
        hereRef.current?.setMap(null);
        hereRef.current = null;
        return;
      }
      if (!hereRef.current) {
        const Ctor = getHereOverlayCtor();
        hereRef.current = new Ctor(userLoc);
        hereRef.current.setMap(map);
      } else {
        hereRef.current.setLatLng(userLoc);
      }
      // 시트가 열려 있으면 그만큼 하단 패딩 주고 프레이밍(you+nearest 가 시트 위 가시영역에).
      computeBounds(coveredRef.current);
    }, [userLoc]);

    // 제스처 모드 변경(모바일 밴드 none ↔ 풀스크린 greedy) 런타임 반영.
    useEffect(() => {
      mapRef.current?.setOptions({ gestureHandling: gesture });
    }, [gesture]);

    // fill: 부모(모바일 스테이지)가 높이 통제 → h-full·라운딩 없음. 아니면 고정 높이 카드.
    // 데스크톱 600px = 리스트 스크롤 영역(max-h-600)과 통일(프로토 v4).
    const wrapH = fill ? "h-full" : "h-[420px] laptop:h-[600px]";
    const rounded = fill ? "" : "rounded-2xl";

    if (!API_KEY) {
      return (
        <div
          className={`flex ${wrapH} w-full items-center justify-center ${rounded} bg-coolgray-10 text-sm text-coolgray-60`}
        >
          {t("Map is temporarily unavailable.")}
        </div>
      );
    }

    return (
      <div className={`relative ${wrapH} w-full`}>
        <div
          ref={containerRef}
          className={`wte-arcade-map h-full w-full overflow-hidden ${rounded} bg-coolgray-10`}
        />
        {!ready && (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-3 ${rounded} bg-coolgray-10 text-sm text-coolgray-50`}
          >
            <Spinner className="size-10" />
            {t("Loading map…")}
          </div>
        )}
      </div>
    );
  },
);

export default ArcadeMap;
